import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Typography
} from '@material-ui/core';
import { TextFormattedNumber } from 'components';
import {
  createPatientsGroupQuoteApi,
  getMarketingSegmentsQuoteQueueApi
} from 'api';
import { useStyles } from '../styles';
import { calculateTotals } from '../utils/calculateTotals';
import QuotedSummaryItem from './QuotedSummaryItem';
import QuoteNotesDialog from './QuoteNotesDialog';


const transformItemsToProducts = (items, quoteProducts, subscriptionOptionsBySku) => {
  return items.map((item) => {
    const product = quoteProducts.find((product) => product.sku === item.sku);

    return {
      id: product.id,
      sku: product.sku,
      name: product.name,
      price: product.price,
      custom_attributes: product.custom_attributes,
      subscription_active: product.custom_attributes.find((attr) => attr.attribute_code === 'subscription_active')?.value,
      subscription_options: subscriptionOptionsBySku?.[product.sku],
      additional_subscription_price: product.custom_attributes.find((attr) => attr.attribute_code === 'additional_subscription_price')?.value,
      savings: product.custom_attributes.find((attr) => attr.attribute_code === 'savings')?.value,
    };
  });
};

const QuotePatientGroupSummaryCard = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { id } = useParams<{id?: string}>();

  const {
    finalPageData,
    subscriptionOptionsBySku,
    patientsGroupCart,
    quoteProducts,
    currentMarketingSegment
  } = useSelector((state: IReduxState) => state.quote);

  const { items = [] } = finalPageData || {};
  const [notesDialog, setNotesDialog] = useState({isOpen: false, dialogType: ''});

  const [totals, setTotals] = useState({
    subtotal: 0,
    discountTotal: 0,
    grandTotal: 0,
    totalSubscription: 0
  });

  useEffect(() => {
    if (items) {
      const transformedProductsData = transformItemsToProducts(items, quoteProducts, subscriptionOptionsBySku);
      const totals = calculateTotals(items, transformedProductsData);
      setTotals(totals);
    }
  }, [items]);

  const handleCreateQuote = async ({quoteNotes, expirationDate, startDate = null, mustUsedDate = null, quoteName, quoteDiscount}) => {
    setNotesDialog({isOpen: false, dialogType: ''});

    const publishData = {
      selectedItems: items,
      expirationDate,
      startDate,
      mustUsedDate,
      quoteName,
      patientNotice: quoteNotes,
      quoteDiscount
    };

    const isCreatedPatientsGroupQuote = await createPatientsGroupQuoteApi({
      dispatch,
      id: currentMarketingSegment.value,
      publishData
    });

    getMarketingSegmentsQuoteQueueApi({dispatch});
    const isPublishedAllItems = publishData.selectedItems.length === patientsGroupCart.length;

    if (isCreatedPatientsGroupQuote && isPublishedAllItems) {
      history.push({
        pathname: '/quoting/queue'
      });
    } else {
      history.push({
        pathname: `/quoting/group/${id}/quote-workspace/summary`
      });
    }
  };

  return (
    <Card className={`${classes.bgBlue} root-card`}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        p={2}
        border={1}
        borderRadius={4}
        borderColor="grey.300"
      >
        <Box display="flex" justifyContent="space-between" width="100%" mt={2}>
          <Button
            className={classes.customQuoteSummaryButton}
            variant="contained"
            color="primary"
            size="large"
            disabled={(Object.keys(items).length === 0)}
            onClick={() => setNotesDialog({isOpen: true, dialogType: 'createQuote'})}
            fullWidth
          >
            Create a Quote
          </Button>
        </Box>
        {notesDialog.isOpen &&
          <QuoteNotesDialog
            isOpen={notesDialog.isOpen}
            onClose={() => setNotesDialog({isOpen: false, dialogType: ''})}
            onConfirm={handleCreateQuote}
            dialogType={notesDialog.dialogType}
            total={totals.grandTotal}
          />
        }
        <Divider style={{ width: '100%', margin: '16px 0' }} />

        <Grid container spacing={3} className="py-4">
          <Grid item xs={12} className="py-2">
            <QuotedSummaryItem
              label="Subtotal"
              value={totals.subtotal}
              prefix="$"
            />
            <QuotedSummaryItem
              label="Total Savings"
              value={totals.discountTotal}
              prefix="$"
            />
            <div className="flex items-center justify-between mt-5 mb-2">
              <Typography variant="h4" className={`font-bold ${classes.customQuoteSummaryTypography}`}>
                TOTAL
              </Typography>
              <Typography variant="h4" align="right" className={`font-bold ${classes.customQuoteSummaryTypography}`}>
                <TextFormattedNumber
                  value={totals.grandTotal}
                  prefix="$"
                />
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Box>
    </Card>
  );
};

export default QuotePatientGroupSummaryCard;
